/**
 * Bento — 4 variants (classic_5, asymmetric_7, stacked_mobile, photo_bento)
 */
import type { BentoContent } from '../schemas/bento';
import {
  Body,
  Eyebrow,
  H2,
  H3,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';
import { getLucideIcon } from './lucide-icons';

interface BP {
  content: BentoContent;
}

interface CellProps {
  title: string;
  description?: string | null;
  icon?: string | null;
  image?: string | null;
  span?: string | null;
  withImage?: boolean;
  style?: React.CSSProperties;
}

function Cell({ title, description, icon, image, withImage = false, style }: CellProps) {
  const Icon = getLucideIcon(icon);
  return (
    <div
      className="ls-card"
      style={{
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      {Icon && (
        <div
          style={{
            width: 48,
            height: 48,
            background: 'var(--ls-accent-soft)',
            color: 'var(--ls-accent)',
            borderRadius: 'var(--ls-radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={24} strokeWidth={2} />
        </div>
      )}
      <H3 style={{ fontSize: '1.125rem' }}>{title}</H3>
      {description && <Body style={{ fontSize: '0.9rem' }}>{description}</Body>}
      {withImage && image && (
        <div style={{ marginTop: 'auto' }}>
          <VisualPlaceholder src={image} aspectRatio="16 / 9" label={title} />
        </div>
      )}
    </div>
  );
}

export function BentoClassic5({ content }: BP) {
  const cells = content.cells.slice(0, 5);
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-bento-grid">
          {cells[0] && <Cell {...cells[0]} withImage style={{ gridColumn: 'span 4' }} />}
          {cells[1] && <Cell {...cells[1]} withImage style={{ gridColumn: 'span 2' }} />}
          {cells[2] && <Cell {...cells[2]} withImage style={{ gridColumn: 'span 2' }} />}
          {cells[3] && <Cell {...cells[3]} withImage style={{ gridColumn: 'span 2' }} />}
          {cells[4] && <Cell {...cells[4]} withImage style={{ gridColumn: 'span 2' }} />}
        </div>
      </div>
    </SectionFrame>
  );
}

export function BentoAsymmetric7({ content }: BP) {
  const cells = content.cells.slice(0, 7);
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-bento-grid" style={{ gridAutoRows: 'minmax(160px, auto)' }}>
          {cells[0] && <Cell {...cells[0]} withImage style={{ gridColumn: 'span 3', gridRow: 'span 2' }} />}
          {cells[1] && <Cell {...cells[1]} withImage style={{ gridColumn: 'span 3' }} />}
          {cells[2] && <Cell {...cells[2]} withImage style={{ gridColumn: 'span 1' }} />}
          {cells[3] && <Cell {...cells[3]} withImage style={{ gridColumn: 'span 2' }} />}
          {cells[4] && <Cell {...cells[4]} withImage style={{ gridColumn: 'span 2' }} />}
          {cells[5] && <Cell {...cells[5]} withImage style={{ gridColumn: 'span 4' }} />}
          {cells[6] && <Cell {...cells[6]} withImage style={{ gridColumn: 'span 2' }} />}
        </div>
      </div>
    </SectionFrame>
  );
}

export function BentoStackedMobile({ content }: BP) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-grid ls-grid-2">
          {content.cells.map((c, i) => (
            <Cell key={i} {...c} withImage />
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function BentoPhotoBento({ content }: BP) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 800 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div className="ls-bento-grid" style={{ gridAutoRows: 'minmax(200px, auto)' }}>
          {content.cells.slice(0, 6).map((c, i) => {
            const span =
              i === 0 ? { gridColumn: 'span 4', gridRow: 'span 2' } :
              i === 1 ? { gridColumn: 'span 2' } :
              i === 2 ? { gridColumn: 'span 2' } :
              i === 3 ? { gridColumn: 'span 3' } :
              i === 4 ? { gridColumn: 'span 3' } :
              { gridColumn: 'span 6' };
            return (
              <div
                key={i}
                style={{
                  borderRadius: 'var(--ls-radius-lg)',
                  overflow: 'hidden',
                  position: 'relative',
                  ...span,
                }}
              >
                <VisualPlaceholder src={c.image} aspectRatio="auto" label={c.title} />
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    padding: 24,
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.6), transparent 60%)',
                    color: '#fff',
                  }}
                >
                  <H3 style={{ color: '#fff', fontSize: '1.125rem' }}>{c.title}</H3>
                  {c.description && (
                    <Body style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem' }}>
                      {c.description}
                    </Body>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SectionFrame>
  );
}

export const bentoVariants = {
  classic_5: BentoClassic5,
  asymmetric_7: BentoAsymmetric7,
  stacked_mobile: BentoStackedMobile,
  photo_bento: BentoPhotoBento,
};
